import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthStore, api } from "@/store";
import { useColWidths } from "@/hooks/useColWidths";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Send, CheckCircle2, Lock, Download, FileSpreadsheet, FileText,
  AlertTriangle, History, ShieldCheck, Loader2, Leaf,
  LockKeyhole, FunctionSquare, Pencil, RotateCcw, Search, Filter,
  ShieldAlert, Unlock, MessageSquare, Edit3, Save, RefreshCw, CalendarClock
} from "lucide-react";
import { toast } from "sonner";
import { t } from "@/i18n/bg";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const statusConfig = {
  DRAFT: { color: "bg-slate-100 text-slate-700 border-slate-200", label: t("draft") },
  SUBMITTED: { color: "bg-blue-50 text-blue-700 border-blue-200", label: t("submitted") },
  APPROVED: { color: "bg-green-50 text-green-700 border-green-200", label: t("approved") },
  LOCKED: { color: "bg-gray-100 text-gray-700 border-gray-200", label: t("locked") },
  NEEDS_CORRECTION: { color: "bg-amber-50 text-amber-700 border-amber-200", label: t("needsCorrection") },
};

function getColLetter(col) {
  let s = "", c = col;
  while (c >= 0) { s = String.fromCharCode((c % 26) + 65) + s; c = Math.floor(c / 26) - 1; }
  return s;
}

export default function ReportEditorPage() {
  const { instanceId } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAdmin = useAuthStore((s) => s.isAdmin);

  const [instance, setInstance] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);
  const [cells, setCells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditSheet, setAuditSheet] = useState("all");
  const [auditUser, setAuditUser] = useState("all");
  const [auditSearch, setAuditSearch] = useState("");
  const [auditType, setAuditType] = useState("all");
  const [auditUsers, setAuditUsers] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [mergedCells, setMergedCells] = useState([]);
  const [correctionNote, setCorrectionNote] = useState("");
  const [adminEditMode, setAdminEditMode] = useState(false);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionText, setCorrectionText] = useState("");
  // Admin cell editor dialog
  const [adminCellDialog, setAdminCellDialog] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState("");
  const [showDeadlineDialog, setShowDeadlineDialog] = useState(false);
  const [deadlineValue, setDeadlineValue] = useState("");
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const gridRef = useRef(null);
  const { handleResizeStart, getColStyle } = useColWidths(
    instanceId ? `report:${instanceId}` : null, activeSheet
  );

  useEffect(() => { loadInstance(); }, [instanceId]);
  useEffect(() => { if (activeSheet) loadSheetData(activeSheet); }, [activeSheet]);

  const loadInstance = async () => {
    try {
      setLoading(true);
      const [instRes, sheetsRes, maintRes] = await Promise.all([
        api.get(`/instances/${instanceId}`), api.get(`/instances/${instanceId}/sheets`),
        api.get("/settings/maintenance"),
      ]);
      setInstance(instRes.data);
      setCorrectionNote(instRes.data.correction_note || "");
      setSheets(sheetsRes.data);
      setMaintenanceMode(maintRes.data.enabled || false);
      setMaintenanceMsg(maintRes.data.message || "");
      if (sheetsRes.data.length > 0) setActiveSheet(sheetsRes.data[0].sheet_name);
    } catch { toast.error(t("loadFailed")); navigate("/dashboard"); }
    finally { setLoading(false); }
  };

  const loadSheetData = async (sheetName) => {
    try {
      setLoading(true); setEditingCell(null); setAdminCellDialog(null);
      const res = await api.get(`/instances/${instanceId}/sheets/${encodeURIComponent(sheetName)}`);
      setCells(res.data.cells || []);
      setMergedCells(res.data.merged_cells || []);
    } catch { toast.error(t("loadFailed")); }
    finally { setLoading(false); }
  };

  // ─── Cell editing (unit user inline edit) ───
  const handleCellClick = (cell) => {
    if (isAdmin() && adminEditMode) {
      setAdminCellDialog({
        cell, value: cell.value ?? "", formula: cell.formula ?? "",
        writable: cell.is_editable || cell.admin_unlocked || false,
        formulaUnlocked: false,
      });
      return;
    }
    if (!cell.is_editable) return;
    if (maintenanceMode && !isAdmin()) { toast.error(t("maintenanceModeActive")); return; }
    setEditingCell(cell.address);
    setEditValue(cell.value ?? "");
  };

  const handleCellSave = async (cell) => {
    if (editValue === String(cell.value ?? "")) { setEditingCell(null); return; }
    setSaving(true);
    try {
      const res = await api.patch(`/instances/${instanceId}/cells`, {
        sheet_name: activeSheet, address: cell.address, value: editValue,
      });
      setCells((prev) => prev.map((c) => {
        if (c.address === cell.address) return { ...c, value: editValue, input_value: editValue };
        const fu = res.data.updated_formulas?.find((uf) => uf.address === c.address && uf.sheet_name === activeSheet);
        if (fu) return { ...c, value: fu.computed_value, computed_value: fu.computed_value };
        return c;
      }));
      setEditingCell(null);
    } catch (err) { toast.error(err.response?.data?.detail || t("saveFailed")); }
    finally { setSaving(false); }
  };

  const handleKeyDown = (e, cell) => {
    if (e.key === "Enter") { e.preventDefault(); handleCellSave(cell); }
    else if (e.key === "Escape") setEditingCell(null);
  };

  // ─── Admin Cell Editor Dialog Save ───
  const handleAdminCellSave = async () => {
    if (!adminCellDialog) return;
    const { cell, value, writable } = adminCellDialog;
    setSaving(true);
    try {
      // Save value only (formulas are locked — edit via Visual Editor)
      const payload = { sheet_name: activeSheet, address: cell.address, value };
      const res = await api.patch(`/instances/${instanceId}/cells`, payload);

      // 2) Toggle writable if changed
      const wasWritable = cell.is_editable || cell.admin_unlocked || false;
      if (writable !== wasWritable) {
        await api.post(`/instances/${instanceId}/admin-unlock-cell`, {
          sheet_name: activeSheet, address: cell.address, unlock: writable,
        });
      }

      // Update local cells
      setCells((prev) => prev.map((c) => {
        if (c.address === cell.address) {
          return {
            ...c,
            value,
            input_value: value,
            admin_unlocked: writable,
            has_admin_override: true,
            is_editable: writable || c.is_input,
          };
        }
        const fu = res.data.updated_formulas?.find((uf) => uf.address === c.address && uf.sheet_name === activeSheet);
        if (fu) return { ...c, value: fu.computed_value, computed_value: fu.computed_value };
        return c;
      }));
      setAdminCellDialog(null);
      toast.success(`${cell.address} ${t("saved")}`);
    } catch (err) { toast.error(err.response?.data?.detail || t("saveFailed")); }
    finally { setSaving(false); }
  };

  // ─── Workflow Actions ───
  const handleSubmit = async () => {
    try {
      await api.post(`/instances/${instanceId}/submit`);
      setInstance((p) => ({ ...p, status: "SUBMITTED" }));
      toast.success(t("reportSubmitted"));
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
  };
  const handleApprove = async () => {
    try {
      await api.post(`/instances/${instanceId}/approve`);
      setInstance((p) => ({ ...p, status: "APPROVED" }));
      toast.success(t("reportApproved"));
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
  };
  const handleLock = async () => {
    try {
      await api.post(`/instances/${instanceId}/lock`);
      setInstance((p) => ({ ...p, status: "LOCKED" }));
      toast.success(t("reportLocked"));
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
  };
  const handleReopen = async () => {
    try {
      await api.post(`/instances/${instanceId}/reject`, { reason: "Reopened" });
      setInstance((p) => ({ ...p, status: "DRAFT" }));
      loadSheetData(activeSheet);
      toast.success(t("reportReopened"));
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
  };
  const handleRequestCorrection = async () => {
    if (!correctionText.trim()) return;
    try {
      await api.post(`/instances/${instanceId}/request-correction`, { note: correctionText });
      setInstance((p) => ({ ...p, status: "NEEDS_CORRECTION", correction_note: correctionText }));
      setCorrectionNote(correctionText);
      setShowCorrectionModal(false);
      setCorrectionText("");
      toast.success(t("correctionRequested"));
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
  };
  const handleExport = async (format = "pdf") => {
    try {
      const res = await api.get(`/instances/${instanceId}/export?format=${format}`, { responseType: "blob" });
      const ext = format === "xlsx" ? "xlsx" : "pdf";
      const a = document.createElement("a"); a.href = window.URL.createObjectURL(new Blob([res.data]));
      a.download = `${instance?.unit_code || "report"}_${instance?.period_year || "2025"}.${ext}`; a.click();
      toast.success(t("downloaded"));
    } catch { toast.error(t("exportFailed")); }
  };
  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await api.post(`/instances/${instanceId}/recalculate`);
      toast.success(t("recalculating"));
      // Reload sheet after a delay to let background recalc finish
      setTimeout(() => {
        if (activeSheet) loadSheetData(activeSheet);
        setRecalculating(false);
        toast.success(t("recalculated"));
      }, 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("failed"));
      setRecalculating(false);
    }
  };
  const handleDeadlineSave = async () => {
    try {
      await api.patch(`/instances/${instanceId}`, { deadline: deadlineValue || null });
      setInstance((p) => ({ ...p, deadline: deadlineValue || null }));
      setShowDeadlineDialog(false);
      toast.success(t("deadlineSaved"));
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
  };

  const handleResetInstance = async () => {
    if (resetConfirmText !== "RESET") return;
    setResetting(true);
    try {
      const res = await api.post(`/instances/${instanceId}/reset`);
      toast.success(`Отчетът на ${res.data.unit_name} е нулиран: ${res.data.created_cells} клетки пресъздадени`);
      setShowResetDialog(false);
      setResetConfirmText("");
      // Reload everything
      const instRes = await api.get(`/instances/${instanceId}`);
      setInstance(instRes.data);
      if (activeSheet) loadSheetData(activeSheet);
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setResetting(false); }
  };
  const handleValidate = async () => {
    try {
      const res = await api.post(`/instances/${instanceId}/validate`);
      setValidationResults(res.data);
      toast.info(`${res.data.length} ${t("issues").toLowerCase()}`);
    } catch { toast.error(t("failed")); }
  };
  const loadAudit = async () => {
    try {
      const params = {};
      if (auditSheet !== "all") params.sheet_name = auditSheet;
      if (auditUser !== "all") params.user_filter = auditUser;
      if (auditSearch) params.address = auditSearch;
      if (auditType !== "all") params.log_type = auditType;
      const [logRes, usersRes] = await Promise.all([
        api.get(`/instances/${instanceId}/audit`, { params }),
        api.get(`/instances/${instanceId}/audit/users`)
      ]);
      setAuditLog(logRes.data);
      setAuditUsers(usersRes.data);
    } catch { toast.error(t("failed")); }
  };

  // ─── Column Resize (handled by useColWidths hook) ───

  if (loading && !instance) return (
    <div className="flex items-center justify-center h-screen bg-[#f8fafc]">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
    </div>
  );

  const stCfg = statusConfig[instance?.status] || statusConfig.DRAFT;
  const maxRow = cells.length > 0 ? Math.max(...cells.map((c) => c.row)) : 0;
  const maxCol = cells.length > 0 ? Math.max(...cells.map((c) => c.col)) : 0;
  const cellMap = {};
  cells.forEach((c) => { cellMap[`${c.row},${c.col}`] = c; });
  const editableCount = cells.filter(c => c.is_editable || c.admin_unlocked).length;
  const formulaCount = cells.filter(c => c.is_formula).length;
  const isUnitUser = user?.role === "UNIT_USER";
  const canUnitSubmit = isUnitUser && (instance?.status === "DRAFT" || instance?.status === "NEEDS_CORRECTION");
  const canAdminApprove = isAdmin() && instance?.status === "SUBMITTED";
  const canAdminCorrect = isAdmin() && (instance?.status === "SUBMITTED" || instance?.status === "APPROVED");
  const canAdminLock = isAdmin() && instance?.status === "APPROVED";

  // Merge map
  const mergeMap = {};
  mergedCells.forEach(mc => {
    try {
      const sr = mc.min_row ?? mc.startRow, sc = mc.min_col ?? mc.startCol;
      const er = mc.max_row ?? mc.endRow, ec = mc.max_col ?? mc.endCol;
      if (!sr || !sc || !er || !ec || sr > er || sc > ec || er > maxRow + 10 || ec > maxCol + 10) return;
      mergeMap[`${sr},${sc}`] = { rowSpan: er - sr + 1, colSpan: ec - sc + 1 };
      for (let r = sr; r <= er; r++) for (let c = sc; c <= ec; c++) {
        if (r === sr && c === sc) continue;
        mergeMap[`${r},${c}`] = "hidden";
      }
    } catch { /* skip */ }
  });

  // ─── Render current (editable) grid ───
  const renderCurrentGrid = (rowCount, colCount) => (
    <table className="border-collapse text-xs" style={{ fontFamily: "'Calibri', 'Segoe UI', sans-serif", tableLayout: "fixed" }}>
      <thead className="sticky top-0 z-10">
        <tr>
          <th className="bg-slate-100 border border-slate-200 px-1 py-1 text-center text-slate-400 font-normal w-10 min-w-[40px]">#</th>
          {Array.from({ length: colCount }, (_, i) => (
            <th key={i} className="bg-slate-100 border border-slate-200 px-2 py-1 text-center text-slate-500 font-semibold relative select-none"
              style={{ fontSize: "11px", ...getColStyle(i) }}>
              {getColLetter(i)}
              <div onMouseDown={(e) => handleResizeStart(i, e)}
                className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize hover:bg-[#14532d]/30 active:bg-[#14532d]/50"
                style={{ touchAction: "none" }} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rowCount }, (_, rowIdx) => {
          const row = rowIdx + 1;
          return (
            <tr key={row}>
              <td className="bg-slate-50 border border-slate-200 px-1 py-0.5 text-center text-slate-400 font-normal" style={{ fontSize: "10px" }}>{row}</td>
              {Array.from({ length: colCount }, (_, colIdx) => {
                const col = colIdx + 1;
                const mergeInfo = mergeMap[`${row},${col}`];
                if (mergeInfo === "hidden") return null;
                const cell = cellMap[`${row},${col}`];
                const isEditing = editingCell === cell?.address;
                const isMerge = mergeInfo && mergeInfo !== "hidden";
                const canEditThisCell = isAdmin() ? adminEditMode : cell?.is_editable;
                let bgColor = "#ffffff";
                let textColor = "#1e293b";
                let cursor = "default";
                let tooltipText = "";
                if (cell) {
                  if (cell.has_admin_override) { bgColor = "#fff7ed"; tooltipText = t("adminOverriddenCell"); }
                  if (cell.admin_unlocked) { bgColor = "#fef3c7"; tooltipText = t("unlockCell") + " (admin)"; }
                  if (cell.is_editable || (isAdmin() && adminEditMode)) {
                    if (!cell.has_admin_override) bgColor = "#ecfdf5";
                    cursor = "pointer";
                    if (!tooltipText) tooltipText = t("editableInput");
                  } else if (cell.is_formula) {
                    bgColor = cell.has_admin_override ? "#fff7ed" : "#f1f5f9";
                    textColor = "#475569";
                    tooltipText = `${t("formula")}: ${cell.formula}`;
                  } else if (cell.is_protected_preset) {
                    bgColor = cell.has_admin_override ? "#fff7ed" : "#fffbeb";
                    textColor = "#78716c";
                    if (!tooltipText) tooltipText = t("protectedPreset");
                  }
                  if (cell.style?.bold) textColor = "#0f172a";
                }
                if (isAdmin() && adminEditMode && cell && !isEditing) {
                  cursor = "pointer";
                  if (!cell.has_admin_override && !cell.is_editable) bgColor = "#fff7ed";
                }
                let displayValue = cell?.value ?? "";
                if (displayValue === "None") displayValue = "";
                if (displayValue === "#UNSUPPORTED!") {
                  bgColor = "#fef2f2"; textColor = "#dc2626";
                } else if (typeof displayValue === "string" && displayValue !== "" && !isNaN(displayValue)) {
                  const num = parseFloat(displayValue);
                  if (!isNaN(num)) displayValue = Number.isInteger(num) ? num.toLocaleString("en-US").replace(/,/g, " ") : num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, " ");
                }
                const tdContent = isEditing ? (
                  <input data-testid={`edit-input-${cell.address}`} type="text" value={editValue}
                    onChange={(e) => setEditValue(e.target.value)} onBlur={() => handleCellSave(cell)}
                    onKeyDown={(e) => handleKeyDown(e, cell)} autoFocus
                    className="w-full h-full border-2 border-[#14532d] rounded px-1 py-0 text-xs bg-white outline-none"
                    style={{ fontFamily: "'Calibri', 'Segoe UI', sans-serif", minWidth: "60px" }} />
                ) : <span className="truncate block">{displayValue}</span>;
                const td = (
                  <td key={col} data-testid={cell ? `cell-${cell.address}` : undefined}
                    onClick={() => cell && canEditThisCell && handleCellClick(cell)}
                    rowSpan={isMerge ? mergeInfo.rowSpan : undefined}
                    colSpan={isMerge ? mergeInfo.colSpan : undefined}
                    className="border border-slate-200 px-1.5 py-0.5 relative group"
                    style={{
                      backgroundColor: bgColor, color: textColor, cursor,
                      fontWeight: cell?.style?.fontWeight === "bold" || cell?.style?.bold ? "700" : cell?.style?.fontWeight === "semibold" ? "600" : "normal",
                      fontSize: cell?.style?.fontSize ? `${Math.min(cell.style.fontSize, 16)}px` : undefined,
                      textAlign: cell?.style?.align || ((cell && !isNaN(cell.value) && cell.value !== "" && cell.value !== null && cell.value !== "None") ? "right" : "left"),
                      ...getColStyle(colIdx),
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      borderLeft: cell?.is_editable ? "2px solid #10b981" : cell?.admin_unlocked ? "2px solid #f59e0b" : undefined,
                    }}
                  >
                    {tdContent}
                    {!isEditing && cell?.is_formula && <FunctionSquare className="w-2.5 h-2.5 absolute top-0 right-0 text-slate-300" />}
                    {!isEditing && cell?.is_protected_preset && !cell?.admin_unlocked && <LockKeyhole className="w-2.5 h-2.5 absolute top-0 right-0 text-amber-300" />}
                    {!isEditing && cell?.admin_unlocked && <ShieldAlert className="w-2.5 h-2.5 absolute top-0 right-0 text-orange-400" />}
                    {!isEditing && cell?.has_admin_override && !cell?.admin_unlocked && <ShieldAlert className="w-2.5 h-2.5 absolute bottom-0 right-0 text-orange-300" />}
                  </td>
                );
                if (tooltipText && !isEditing) {
                  return (
                    <Tooltip key={col}>
                      <TooltipTrigger asChild>{td}</TooltipTrigger>
                      <TooltipContent side="top" className="text-[10px] max-w-[200px]">{tooltipText}</TooltipContent>
                    </Tooltip>
                  );
                }
                return td;
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div data-testid="report-editor" className="flex flex-col h-screen bg-[#f8fafc]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-slate-200 shadow-sm shrink-0">
        <div className="flex items-center gap-3">
          <Button data-testid="back-to-dashboard" variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="h-8 text-slate-600">
            <ArrowLeft className="w-4 h-4 mr-1" /> {t("back")}
          </Button>
          <div className="h-5 w-px bg-slate-200" />
          <Leaf className="w-4 h-4 text-[#14532d]" />
          <span className="text-sm font-medium text-slate-800">{instance?.unit_name}</span>
          <span data-testid="instance-status" className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${stCfg.color}`}>{stCfg.label}</span>
          {instance?.deadline && (() => {
            const now = new Date(); now.setHours(0,0,0,0);
            const dl = new Date(instance.deadline); dl.setHours(0,0,0,0);
            const diff = Math.ceil((dl - now) / 86400000);
            const isDone = instance?.status === "APPROVED" || instance?.status === "LOCKED";
            if (isDone) return <span className="text-[10px] text-slate-400 ml-1">{t("deadline")}: {dl.toLocaleDateString("bg-BG")}</span>;
            if (diff < 0) return <span data-testid="deadline-badge" className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-600 border border-red-200 ml-1">{t("deadlineExpired")}</span>;
            if (diff <= 3) return <span data-testid="deadline-badge" className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-600 border border-amber-200 ml-1">{diff} {t("daysLeft")}</span>;
            return <span className="text-[10px] text-slate-400 ml-1">{t("deadline")}: {dl.toLocaleDateString("bg-BG")}</span>;
          })()}
          {saving && <span className="flex items-center gap-1 text-xs text-amber-600"><Loader2 className="w-3 h-3 animate-spin" /> {t("saving")}</span>}
          <span className="text-[10px] text-slate-400 ml-2">
            <Pencil className="w-3 h-3 inline mr-0.5" />{editableCount} {t("editable")}
            <FunctionSquare className="w-3 h-3 inline ml-2 mr-0.5" />{formulaCount} {t("formulas")}
          </span>
          {isAdmin() && adminEditMode && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700 border border-orange-200">
              <Edit3 className="w-3 h-3 mr-1" /> {t("adminEditModeActive")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="validate-button" variant="outline" size="sm" onClick={handleValidate} className="h-8 text-xs"><ShieldCheck className="w-3.5 h-3.5 mr-1" /> {t("validate")}</Button>
          <Button data-testid="recalculate-button" variant="outline" size="sm" onClick={handleRecalculate} disabled={recalculating}
            className="h-8 text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50">
            {recalculating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            {t("recalculate")}
          </Button>
          <Sheet>
            <SheetTrigger asChild><Button data-testid="audit-button" variant="outline" size="sm" onClick={loadAudit} className="h-8 text-xs"><History className="w-3.5 h-3.5 mr-1" /> {t("changelog")}</Button></SheetTrigger>
            <SheetContent className="w-[450px] overflow-y-auto">
              <SheetHeader><SheetTitle>{t("changelog")}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Select value={auditType} onValueChange={setAuditType}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("auditTrail")}</SelectItem>
                      <SelectItem value="cell">{t("cellChanges")}</SelectItem>
                      <SelectItem value="workflow">{t("workflowChanges")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={auditSheet} onValueChange={setAuditSheet}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("allSheets")}</SelectItem>
                      {sheets.map(s => <SelectItem key={s.sheet_name} value={s.sheet_name}>{s.sheet_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {isAdmin() && (
                    <Select value={auditUser} onValueChange={setAuditUser}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("allUsers")}</SelectItem>
                        {auditUsers.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <div className="relative">
                    <Search className="w-3 h-3 absolute left-2 top-2.5 text-slate-400" />
                    <Input data-testid="audit-search" placeholder={t("searchCell")} value={auditSearch}
                      onChange={(e) => setAuditSearch(e.target.value)} className="h-8 text-xs pl-7" />
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={loadAudit} className="w-full h-8 text-xs">
                  <Filter className="w-3 h-3 mr-1" /> {t("filterByPeriod").split(" ")[0]}
                </Button>
                {auditLog.length === 0 ? <p className="text-sm text-muted-foreground py-4">{t("noLogs")}</p> :
                  auditLog.map((log) => (
                    <div key={log.id} className="text-xs border border-slate-100 rounded-md p-3 bg-slate-50">
                      {log.action ? (
                        <div>
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium mb-1 ${
                            log.action === "APPROVE" ? "bg-green-100 text-green-700" :
                            log.action === "SUBMIT" ? "bg-blue-100 text-blue-700" :
                            log.action === "LOCK" ? "bg-gray-100 text-gray-700" :
                            log.action === "REJECT" || log.action === "REQUEST_CORRECTION" ? "bg-amber-100 text-amber-700" :
                            log.action === "ADMIN_UNLOCK" || log.action === "ADMIN_LOCK" ? "bg-orange-100 text-orange-700" :
                            "bg-slate-100 text-slate-700"
                          }`}>{log.action}</span>
                          {log.reason && <span className="text-slate-500 ml-1">&mdash; {log.reason}</span>}
                        </div>
                      ) : (
                        <div>
                          <span className="font-mono text-[#14532d]">{log.sheet_name}!{log.address}</span>
                          {log.is_admin_override && <span className="ml-1 text-[9px] text-orange-600 font-medium">(admin)</span>}
                          <div className="mt-1 flex items-center gap-1">
                            <span className="text-red-500 line-through">{log.old_value || "\u2014"}</span>
                            <span className="text-slate-300 mx-0.5">&rarr;</span>
                            <span className="text-green-600 font-medium">{log.new_value || "\u2014"}</span>
                          </div>
                        </div>
                      )}
                      <div className="mt-1.5 text-slate-400">{log.user_email} &middot; {new Date(log.changed_at).toLocaleString("bg-BG")}</div>
                    </div>
                  ))}
              </div>
            </SheetContent>
          </Sheet>
          {validationResults.length > 0 && (
            <Sheet>
              <SheetTrigger asChild><Button variant="outline" size="sm" className="h-8 text-xs border-amber-200 text-amber-700 bg-amber-50"><AlertTriangle className="w-3.5 h-3.5 mr-1" /> {validationResults.length}</Button></SheetTrigger>
              <SheetContent className="w-[400px] overflow-y-auto">
                <SheetHeader><SheetTitle>{t("validationResults")}</SheetTitle></SheetHeader>
                <div className="mt-4 space-y-3">{validationResults.map((vr) => (
                  <div key={vr.id} className={`text-xs border rounded-md p-3 ${vr.severity==="ERROR"?"border-red-200 bg-red-50":"border-amber-200 bg-amber-50"}`}>
                    <div className="flex items-center gap-1.5"><AlertTriangle className={`w-3.5 h-3.5 ${vr.severity==="ERROR"?"text-red-500":"text-amber-500"}`} /><span className="font-medium">{vr.severity}</span>{vr.sheet_name && <span className="text-slate-500">{vr.sheet_name}!{vr.address}</span>}</div>
                    <p className="mt-1 text-slate-600">{vr.message}</p>
                  </div>
                ))}</div>
              </SheetContent>
            </Sheet>
          )}
          <Button data-testid="export-xlsx-button" variant="outline" size="sm" onClick={() => handleExport("xlsx")} className="h-8 text-xs"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> XLSX</Button>
          <Button data-testid="export-pdf-button" variant="outline" size="sm" onClick={() => handleExport("pdf")} className="h-8 text-xs"><FileText className="w-3.5 h-3.5 mr-1" /> PDF</Button>
          {isAdmin() && (
            <Button data-testid="edit-deadline-button" variant="outline" size="sm"
              onClick={() => { setDeadlineValue(instance?.deadline || ""); setShowDeadlineDialog(true); }}
              className="h-8 text-xs text-slate-500 border-slate-200 hover:bg-slate-50">
              <CalendarClock className="w-3.5 h-3.5 mr-1" /> {t("deadline")}
            </Button>
          )}

          {/* Role-based workflow buttons */}
          {canUnitSubmit && (
            <Button data-testid="submit-button" size="sm" onClick={handleSubmit} className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white">
              <Send className="w-3.5 h-3.5 mr-1" /> {t("submit")}
            </Button>
          )}
          {isAdmin() && (instance?.status === "DRAFT" || instance?.status === "NEEDS_CORRECTION") && (
            <span data-testid="awaiting-submission-text" className="text-xs text-slate-500 italic px-2 py-1">{t("awaitingSubmission")}</span>
          )}
          {canAdminApprove && (
            <Button data-testid="approve-button" size="sm" onClick={handleApprove} className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {t("approve")}
            </Button>
          )}
          {canAdminCorrect && (
            <Button data-testid="request-correction-button" size="sm" variant="outline"
              onClick={() => setShowCorrectionModal(true)}
              className="h-8 text-xs text-amber-600 border-amber-200 hover:bg-amber-50">
              <MessageSquare className="w-3.5 h-3.5 mr-1" /> {t("requestCorrection")}
            </Button>
          )}
          {canAdminLock && (
            <Button data-testid="lock-button" size="sm" onClick={handleLock} className="h-8 text-xs bg-gray-600 hover:bg-gray-700 text-white">
              <Lock className="w-3.5 h-3.5 mr-1" /> {t("lock")}
            </Button>
          )}
          {isAdmin() && (instance?.status === "APPROVED" || instance?.status === "LOCKED") && (
            <Button data-testid="reopen-button" variant="outline" size="sm" onClick={handleReopen} className="h-8 text-xs text-amber-600 border-amber-200 hover:bg-amber-50">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> {t("reopen")}
            </Button>
          )}
          {isAdmin() && instance?.status !== "LOCKED" && (
            <Button data-testid="admin-edit-toggle" size="sm" variant={adminEditMode ? "default" : "outline"}
              onClick={() => setAdminEditMode(!adminEditMode)}
              className={`h-8 text-xs ${adminEditMode ? "bg-orange-500 hover:bg-orange-600 text-white" : "text-orange-600 border-orange-200 hover:bg-orange-50"}`}>
              <Edit3 className="w-3.5 h-3.5 mr-1" /> {t("adminEditMode")}
            </Button>
          )}
          {isAdmin() && (
            <Button data-testid="reset-instance-button" variant="outline" size="sm"
              onClick={() => setShowResetDialog(true)}
              className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Нулиране
            </Button>
          )}
        </div>
      </div>

      {/* Maintenance Mode Banner */}
      {maintenanceMode && !isAdmin() && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-red-50 border-b border-red-200 shrink-0">
          <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
          <p className="text-xs font-medium text-red-800">{maintenanceMsg || t("maintenanceModeActive")}</p>
        </div>
      )}

      {/* Correction Note Banner */}
      {instance?.status === "NEEDS_CORRECTION" && correctionNote && (
        <div data-testid="correction-note-banner" className="flex items-start gap-3 px-4 py-2.5 bg-amber-50 border-b border-amber-200 shrink-0">
          <MessageSquare className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-medium text-amber-800">{t("correctionNote")}:</p>
            <p className="text-xs text-amber-700 mt-0.5">{correctionNote}</p>
          </div>
        </div>
      )}

      {/* Sheet Tabs */}
      <div className="flex items-center bg-white border-b border-slate-200 px-2 overflow-x-auto shrink-0">
        {sheets.map((s) => (
          <button key={s.sheet_name} data-testid={`sheet-tab-${s.order_index}`}
            onClick={() => setActiveSheet(s.sheet_name)}
            className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeSheet === s.sheet_name ? "border-[#14532d] text-[#14532d] bg-emerald-50/50" : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
            }`}>{s.sheet_name}</button>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-1.5 bg-white border-b border-slate-100 text-[10px] text-slate-500 shrink-0">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-emerald-300 bg-emerald-50 inline-block" /> {t("editableInput")}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-slate-200 bg-slate-100 inline-block" /> {t("formulaAutoCalc")}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-amber-200 bg-amber-50/50 inline-block" /> {t("protectedPreset")}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-slate-200 bg-white inline-block" /> {t("static")}</span>
        {isAdmin() && adminEditMode && <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-orange-300 bg-orange-100 inline-block" /> {t("adminOverride")}</span>}
      </div>

      {/* Grid */}
      <div data-testid="spreadsheet-grid" className="flex-1 overflow-hidden flex">
        <TooltipProvider delayDuration={200}>
        {loading ? (
          <div className="flex items-center justify-center h-64 w-full"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : cells.length === 0 ? (
          <div className="flex items-center justify-center h-64 w-full text-muted-foreground">{t("noData")}</div>
        ) : (
          <div className="flex-1 overflow-auto" ref={gridRef}>
            {renderCurrentGrid(maxRow, maxCol)}
          </div>
        )}
        </TooltipProvider>
      </div>

      {/* ─── Admin Cell Editor Dialog ─── */}
      <Dialog open={!!adminCellDialog} onOpenChange={(open) => { if (!open) setAdminCellDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Edit3 className="w-4 h-4 text-orange-500" />
              <span className="font-mono">{activeSheet}!{adminCellDialog?.cell?.address}</span>
            </DialogTitle>
          </DialogHeader>
          {adminCellDialog && (
            <div className="space-y-4 pt-1">
              {/* Cell type badges */}
              <div className="flex items-center gap-2">
                {adminCellDialog.cell.is_formula && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                    <FunctionSquare className="w-3 h-3 mr-1" /> {t("formula")}
                  </span>
                )}
                {adminCellDialog.cell.is_protected_preset && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-600 border border-amber-200">
                    <LockKeyhole className="w-3 h-3 mr-1" /> {t("protectedPreset")}
                  </span>
                )}
                {adminCellDialog.cell.has_admin_override && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-orange-50 text-orange-600 border border-orange-200">
                    <ShieldAlert className="w-3 h-3 mr-1" /> {t("adminOverride")}
                  </span>
                )}
              </div>

              {/* Value */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">{t("cellValue")}</Label>
                <Input data-testid="admin-cell-value-input"
                  value={adminCellDialog.value}
                  onChange={(e) => setAdminCellDialog(prev => ({ ...prev, value: e.target.value }))}
                  className="h-9 text-sm font-mono" placeholder="..." />
              </div>

              {/* Formula — read-only display (editing only via Visual Editor) */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">
                  <FunctionSquare className="w-3 h-3 inline mr-1" />{t("formulaLabel")}
                </Label>
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded border border-slate-200">
                  <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <span className="text-xs text-slate-500 font-mono flex-1 truncate">{adminCellDialog.formula || "—"}</span>
                </div>
                <p className="text-[10px] text-slate-400">Формулите се редактират само от Визуалния редактор в Администрация.</p>
              </div>

              {/* Writable toggle */}
              <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2">
                  {adminCellDialog.writable ? <Unlock className="w-4 h-4 text-green-600" /> : <Lock className="w-4 h-4 text-slate-400" />}
                  <Label className="text-xs font-medium text-slate-700 cursor-pointer" htmlFor="writable-toggle">
                    {t("writableForUnit")}
                  </Label>
                </div>
                <Switch data-testid="admin-cell-writable-toggle" id="writable-toggle"
                  checked={adminCellDialog.writable}
                  onCheckedChange={(v) => setAdminCellDialog(prev => ({ ...prev, writable: v }))} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setAdminCellDialog(null)}>{t("close")}</Button>
            <Button data-testid="admin-cell-save-button" size="sm" onClick={handleAdminCellSave} disabled={saving}
              className="bg-orange-500 hover:bg-orange-600 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Correction Request Modal ─── */}
      <Dialog open={showCorrectionModal} onOpenChange={setShowCorrectionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-amber-500" /> {t("requestCorrection")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-sm text-slate-600">{t("correctionNoteLabel")}</p>
            <Textarea data-testid="correction-note-input"
              value={correctionText} onChange={(e) => setCorrectionText(e.target.value)}
              placeholder={t("correctionNotePlaceholder")} rows={4} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCorrectionModal(false)}>{t("close")}</Button>
            <Button data-testid="send-correction-button"
              onClick={handleRequestCorrection} disabled={!correctionText.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-white">
              <Send className="w-4 h-4 mr-1" /> {t("requestCorrection")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Deadline Edit Dialog ─── */}
      <Dialog open={showDeadlineDialog} onOpenChange={setShowDeadlineDialog}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="w-4 h-4 text-slate-500" /> {t("editDeadline")}
            </DialogTitle>
          </DialogHeader>
          <div className="pt-2">
            <Input data-testid="deadline-edit-input" type="date" value={deadlineValue}
              onChange={(e) => setDeadlineValue(e.target.value)} className="h-10" />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowDeadlineDialog(false)}>{t("close")}</Button>
            <Button data-testid="deadline-save-button" size="sm" onClick={handleDeadlineSave}
              className="bg-[#14532d] hover:bg-[#166534] text-white">
              <Save className="w-4 h-4 mr-1" /> {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Instance Dialog */}
      <Dialog open={showResetDialog} onOpenChange={(open) => { if (!open) { setShowResetDialog(false); setResetConfirmText(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Нулиране на отчет
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800 space-y-2">
              <p className="font-semibold">Внимание! Тази операция ще:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Изтрие ВСИЧКИ въведени данни за <span className="font-bold">{instance?.unit_name}</span></li>
                <li>Пресъздаде клетките от шаблона (чист старт)</li>
                <li>Преизчисли всички формули</li>
                <li>Върне статуса на "Чернова"</li>
              </ul>
              <p className="font-semibold text-red-900">Засяга САМО този отчет, не другите звена.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Въведете <span className="font-mono font-bold">RESET</span> за потвърждение:</Label>
              <Input data-testid="reset-confirm-input" value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                className="h-9 font-mono text-center" placeholder="RESET" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setShowResetDialog(false); setResetConfirmText(""); }}>Отказ</Button>
            <Button data-testid="reset-confirm-button" size="sm" onClick={handleResetInstance}
              disabled={resetConfirmText !== "RESET" || resetting}
              className="bg-red-600 hover:bg-red-700 text-white">
              {resetting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
              Нулирай отчета
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
